import AppErrorBoundary from "components/ErrorBoundary";
import { useDispatch, useSelector } from "react-redux";
import normal_u16 from "assets/normal_u16.svg";
import star_isolated from "assets/star_Isolated.svg";
import { GlobalProvider, Flex } from "@enaep-ng/benchmark";
import { AspectRatioBox } from "@enaep-ng/benchmark-lab";
import PlugIcon from "./assets/plug_icon.svg";
import {
  createSetAssessmentModeAction,
  createSetExitAction,
  createSetHomeAction,
  createSetSwitchLanguage
} from "state/reducer";
import { useSuppressKeyboardZoom } from "hooks/useSupressKeyboardZoom";
import Header from "lib/components/HeaderV2";
import {
  AuthenticationView,
  ErrorLoadingView,
  LoadingView,
  ModeSelectionView,
  DeviceValidationResultsView,
} from "./views";
import Footer from "lib/components/Footer";
import UpdaterView from "views/UpdaterView";
import { IADPState } from "Types/AppStateTypes";
import {
  LoadingProps,
  AuthenticationProps,
  ModeSelectionProps,
  ResultsProps,
  ResultItem,
} from "Types/ViewTypes";
import { HeaderProps, ConnectionType } from "lib/types";
import { DeviceValidationLoadingView } from "views/DeviceValidationLoadingView";
import questionMark from "./assets/question_mark_black.svg";
import raiseHand from "./assets/squareraisehand.svg";
import { useDedupEffect } from "hooks/useDedupEffect";
import { useEffect, useRef, useState } from "react";
import { WarningDialogue } from "lib/components";
import { useInitilizeApp } from "hooks";
import FailureView from "views/FailureView";

function App() {
  useInitilizeApp();
  useSuppressKeyboardZoom();
  const state = useSelector(
    (state: { rootState: IADPState }) => state.rootState
  );
  const deviceReadinessInfo = useSelector(
    (state: { rootState: IADPState }) =>
      state.rootState.deviceReadinessState.deviceReadinessInfo
  );
  const dispatch = useDispatch();
  const cacheValidationStarted = useRef(false);

  const selectedLanguage = {
    selectedLocale: state.appState.selectedLanguage,
    content: {
      instructions: "please select a language",
      switch: { englishVersion: "English", spanishVersion: "Espanol" },
    },
    action: (selectedLocale: "en" | "es") => {
      dispatch(createSetSwitchLanguage(selectedLocale));
    },
  };

  const [showLowBatteryPopup, setShowLowBatteryPopup] = useState(false);
  const [lowBatteryPopupShown, setLowBatteryPopupShown] = useState(false);

  useEffect(() => {
    const isLowBattery = state.deviceState.batteryState?.level < 0.3;
    const isCharging = state.deviceState.batteryState?.charging;

    if (isLowBattery && !isCharging && !lowBatteryPopupShown) {
      setShowLowBatteryPopup(true);
      setLowBatteryPopupShown(true);
    }

    // resets the popup state when the battery level is above 30% for future low battery warnings
    if (state.deviceState.batteryState?.level >= 0.3) {
      setLowBatteryPopupShown(false);
    }

  }, [state.deviceState.batteryState?.level, state.deviceState.batteryState?.charging]);

  const headerProps: HeaderProps = {
    BatteryLevel: {
      level: state.deviceState.batteryState?.level,
      state: state.deviceState.batteryState?.charging
        ? "Charging"
        : state.deviceState.batteryState?.level > 0.5
        ? "Full"
        : state.deviceState.batteryState?.level < 0.3
        ? "Danger"
        : "Half",
      v2Check: false,
    },
    ConnectionState:
      state.sessionState.networkConnectionState === "Connected"
        ? (["Connected"] as ConnectionType[])
        : (["N/A"] as ConnectionType[]),
    exitAction: () => {
      dispatch(createSetExitAction());
    },
    exitText: state.currentLanguageContent?.header?.exit || "Exit",
    homeText: state.currentLanguageContent?.header?.home || "",
    enLangText: state.currentLanguageContent?.header?.enLangText || "",
    esLangText: state.currentLanguageContent?.header?.esLangText || "",
    homeAction: () => {
      dispatch(createSetHomeAction());
    },
    Link: undefined,
    LanguageSwitch: selectedLanguage,
    Modal: undefined,
    connectionMode: {
      text: "ConnectionMode",
      routerState:
        state.sessionState.networkConnectionState === "Connected"
          ? ("Connected" as ConnectionType)
          : ("N/A" as ConnectionType),
    },
    releaseVersion: {
      title: state.currentLanguageContent?.header?.version || "",
      version: state.appState.releaseVersion || "",
      versionColors: state.appState.releaseColors,
      versionBorderColors: state.appState.releaseBorder,
    },
    homeButtonEnabled: state.sessionState.homeButtonEnabled,
    exitButtonEnabled: state.sessionState.exitButtonEnabled,
  };

  useDedupEffect(() => {
    if (state.cacheValidation.validationStatus === "InProgress") {
      cacheValidationStarted.current = true;
    }
  }, [cacheValidationStarted]);
  const renderCurrentView = () => {
    switch (
      state.appState.currentView
    ) {
      case "LoadingView": {
        const loadingProps: LoadingProps = {
          title: state.currentLanguageContent?.initialWait.title || "",
          message: state.currentLanguageContent?.initialWait.message || "",
          subTitle: state.currentLanguageContent?.initialWait.subTitle || "",
        };
        return <LoadingView {...loadingProps} />;
      }

      case "DeviceValidationView": {
        return <DeviceValidationLoadingView />;
      }

      case "AccessCodeValidationView": {
        const authenticationProps: AuthenticationProps = {
          title: state.currentLanguageContent?.accessCode.title || "",
          subTitle: state.currentLanguageContent?.accessCode.subTitle || "",
          input: {
            label: state.currentLanguageContent?.accessCode.inputLabel || "",
            prefix: state.currentLanguageContent?.accessCode.prefix || "",
            verifying: state.currentLanguageContent?.accessCode.verifying || "",
            errorMessage:
              state.currentLanguageContent?.accessCode.codeErrorText || "",
          },
          submitButton: {
            label: state.currentLanguageContent?.actions.submit || "",
          },
          continueButton: {
            label: state.currentLanguageContent?.actions.continue || "",
          },
        };
        return <AuthenticationView {...authenticationProps} />;
      }

      case "VerificationCodeValidationView": {
        const authenticationProps: AuthenticationProps = {
          title: state.currentLanguageContent?.verificationCode.title || "",
          subTitle:
            state.currentLanguageContent?.verificationCode.subTitle || "",
          input: {
            label:
              state.currentLanguageContent?.verificationCode.inputLabel || "",
            prefix: state.currentLanguageContent?.verificationCode.prefix || "",
            verifying:
              state.currentLanguageContent?.verificationCode.verifying || "",
            errorMessage:
              state.currentLanguageContent?.verificationCode.codeErrorText ||
              "",
          },
          submitButton: {
            label: state.currentLanguageContent?.actions.submit || "",
          },
          continueButton: {
            label: state.currentLanguageContent?.actions.continue || "",
          },
        };
        return <AuthenticationView {...authenticationProps} />;
      }

      case "ModeSelectionsView": {
        const modeSelectionsProps: ModeSelectionProps = {
          title: state.currentLanguageContent?.modeSelectionView.title || "",
          actions: [
            {
              label:
                state.currentLanguageContent?.modeSelectionView
                  .preAssessmentCheck.label || "",
              content:
                state.currentLanguageContent?.modeSelectionView
                  .preAssessmentCheck.content || "",
              actionDescription:
                state.currentLanguageContent?.modeSelectionView
                  .preAssessmentCheck.actionDescription || "",
              iconSrc: normal_u16,
              action: () => {
                dispatch(createSetAssessmentModeAction("Verify"));
              },
            },
            {
              label:
                state.currentLanguageContent?.modeSelectionView.assessmentDay
                  .label || "",
              content:
                state.currentLanguageContent?.modeSelectionView.assessmentDay
                  .content || "",
              actionDescription:
                state.currentLanguageContent?.modeSelectionView.assessmentDay
                  .actionDescription || "",
              iconSrc: star_isolated,
              action: () => {
                dispatch(createSetAssessmentModeAction("Assess"));
              },
            },
          ],
        };
        return <ModeSelectionView {...modeSelectionsProps} />;
      }
      case "FailureView": {
        return <FailureView />
      }
      case "WarningView": {
        return <ErrorLoadingView />;
      }
      case "DeviceValidationSuccessView": {
        const resultsSuccessProps: ResultsProps = {
          type: "success",
          title:
            state.currentLanguageContent?.deviceValidationSuccess.title || "",
          subtitle:
            state.currentLanguageContent?.deviceValidationSuccess.subTitle ||
            "",
          warning: {
            title:
              state.currentLanguageContent?.deviceValidationSuccess.notice
                .title || "",
            message:
              state.currentLanguageContent?.deviceValidationSuccess.notice
                .message || "",
            icon: PlugIcon,
          },
          button: {
            label: state.currentLanguageContent?.actions.finished || "",
            action: () => {
              dispatch(createSetExitAction());
            },
          },
        };
        return <DeviceValidationResultsView {...resultsSuccessProps} />;
      }

      case "DeviceValidationFailureView": {
        const failureresults = [] as ResultItem[];
        if (deviceReadinessInfo) {
          deviceReadinessInfo.failureReason.forEach((reason) => {
            failureresults.push({ message: reason?.messageOnFailure });
          });
        }
        const resultsSuccessProps: ResultsProps = {
          type: "error",
          title:
            state.currentLanguageContent?.deviceValidationFailure.title || "",
          subtitle:
            state.currentLanguageContent?.deviceValidationFailure.subTitle ||
            "",
          expandBtn: {
            hideLabel: state.currentLanguageContent?.actions.hideDetails || "",
            showLabel: state.currentLanguageContent?.actions.showDetails || "",
          },
          warning: {
            title:
              state.sessionState.appAccessedFor === "Assess"
                ? state.currentLanguageContent?.deviceValidationFailure.notice
                    .assessTitle || ""
                : state.currentLanguageContent?.deviceValidationFailure.notice
                    .title || "",
            message:
              state.currentLanguageContent?.deviceValidationFailure.notice
                .message || "",
            icon:
              state.sessionState.appAccessedFor === "Assess"
                ? raiseHand
                : questionMark,
          },
          results: failureresults,
        };
        if(state.sessionState.appAccessedFor === "Verify"){
          resultsSuccessProps.button =  {
            label: state.currentLanguageContent?.actions.exit || "",
            action: () => {
              dispatch(createSetExitAction());
            },
          }
        }
        return <DeviceValidationResultsView {...resultsSuccessProps} />;
      }

      case "UpdaterView": {
        return <UpdaterView />;
      }
    }
  };
  return (
    <GlobalProvider>
      <Flex
        sx={{
          height: "calc(100vh * var(--zoom))",
          minHeight: 0,
          flexDirection: "column",
          overflow: "hidden",
          alignItems: "center",
        }}
      >
        <AspectRatioBox
          targetWidth={1366}
          targetHeight={768}
          style={{
            overflow: "hidden",
          }}
          fluid={true}
        >
          <AppErrorBoundary hasError={false}>
            <Header {...headerProps} />
            {showLowBatteryPopup && (
              <WarningDialogue
                title={
                  state.currentLanguageContent?.header?.BatteryWarning?.title ||
                  ""
                }
                subTitle={
                  state.currentLanguageContent?.header?.BatteryWarning
                    ?.subTitle || ""
                }
                closeAction={() => setShowLowBatteryPopup(false)}
              />
            )}
            <div>{renderCurrentView()}</div>
            {<Footer primary={true} />}
          </AppErrorBoundary>
        </AspectRatioBox>
      </Flex>
    </GlobalProvider>
  );
}
export default App;
